/**
 * Shared primitives for the Care Plan iOS tabs (Wellness / Meds / Vaccines).
 * Mirrors the iOS design: collapsible glass cards, emoji-tile section headers,
 * uppercase status pills, gradient action buttons.
 */
import React, { useState } from 'react';
import { Box, Typography, Collapse, SxProps, Theme } from '@mui/material';
import { ExpandMore as ChevronIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { glassSurface, glowShadow } from '../../../theme/uiTokens';
import { useTheme } from '@mui/material/styles';

export const iosColors = {
  green: '#34D399',
  mint: '#6EE7B7',
  orange: '#FB923C',
  amber: '#FBBF24',
  red: '#F87171',
  blue: '#60A5FA',
  indigo: '#818CF8',
  cyan: '#22D3EE',
  teal: '#14B8A6',
  rose: '#F472B6',
  pink: '#EC4899',
  purple: '#C084FC',
  violet: '#A78BFA',
  slate: '#94A3B8',
  slateDim: '#64748B',
};

export function alphaHex(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Emoji/icon tile (36x36 rounded rect with tinted bg) used in section headers. */
export const EmojiTile: React.FC<{ emoji?: string; icon?: React.ReactNode; tint: string; size?: number }> = ({
  emoji,
  icon,
  tint,
  size = 36,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: `${Math.round(size * 0.28)}px`,
      bgcolor: alphaHex(tint, 0.22),
      color: tint,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${Math.round(size * 0.55)}px`,
      flexShrink: 0,
      '& > svg': { fontSize: Math.round(size * 0.55) },
    }}
  >
    {emoji ?? icon}
  </Box>
);

/** Uppercase status pill — iOS .caption2.bold with tracking. */
export const StatusPill: React.FC<{ label: string; tint: string; size?: 'sm' | 'md' }> = ({
  label,
  tint,
  size = 'md',
}) => {
  const paddingX = size === 'sm' ? 0.85 : 1.1;
  const paddingY = size === 'sm' ? 0.25 : 0.4;
  const fontSize = size === 'sm' ? '10px' : '11px';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: paddingX,
        py: paddingY,
        borderRadius: 999,
        // Embossed iOS pill — no border. Mode-aware tint with an inset
        // top highlight (60% white in light, 6% in dark) + a subtle
        // tinted drop shadow for depth.
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? alphaHex(tint, 0.22)
            : alphaHex(tint, 0.16),
        color: tint,
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? `inset 0 1px 0 ${alphaHex('#ffffff', 0.06)}`
            : `inset 0 1px 0 ${alphaHex('#ffffff', 0.6)}, 0 1px 1px ${alphaHex(tint, 0.10)}`,
      }}
    >
      {label}
    </Box>
  );
};

/** Status dot (6–8px circle, optional pulse for overdue). */
export const StatusDot: React.FC<{ tint: string; pulse?: boolean; size?: number }> = ({
  tint,
  pulse = false,
  size = 8,
}) => (
  <Box
    component={motion.div}
    animate={pulse ? { scale: [1, 1.25, 1], opacity: [1, 0.65, 1] } : undefined}
    transition={pulse ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' } : undefined}
    sx={{ width: size, height: size, borderRadius: '50%', bgcolor: tint, flexShrink: 0 }}
  />
);

/** A tinted "chip" meta row cell (like "7d left" or "Once daily"). */
export const MetaChip: React.FC<{ icon?: React.ReactNode; label: string; tint?: string }> = ({
  icon,
  label,
  tint,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.35,
        borderRadius: 999,
        bgcolor: tint ? alphaHex(tint, 0.14) : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
        color: tint ?? (isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.65)'),
        fontSize: '12px',
        fontWeight: 600,
        '& > svg': { fontSize: 14 },
      }}
    >
      {icon}
      {label}
    </Box>
  );
};

/** Gradient button — iOS-style primary CTA (e.g. "Mark dose given", "Log Symptom"). */
export const GradientButton: React.FC<{
  children: React.ReactNode;
  tint?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  variant?: 'solid' | 'outline';
}> = ({ children, tint = iosColors.green, onClick, icon, disabled, fullWidth = true, variant = 'solid' }) => {
  const isSolid = variant === 'solid';
  const gradient = `linear-gradient(135deg, ${tint} 0%, ${alphaHex(tint, 0.7)} 100%)`;
  return (
    <Box
      component="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: fullWidth ? '100%' : 'auto',
        border: isSolid ? 'none' : '1px solid',
        borderColor: alphaHex(tint, 0.4),
        borderRadius: 999,
        py: 1.25,
        px: 2.5,
        background: isSolid ? gradient : 'transparent',
        color: isSolid ? '#fff' : tint,
        fontSize: '0.9375rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        boxShadow: isSolid ? `0 4px 12px ${alphaHex(tint, 0.28)}` : 'none',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover:not(:disabled)': {
          transform: 'translateY(-1px)',
          boxShadow: isSolid ? `0 6px 16px ${alphaHex(tint, 0.35)}` : 'none',
        },
        '&:active': { transform: 'translateY(0)' },
        '& > svg': { fontSize: 18 },
      }}
    >
      {icon}
      {children}
    </Box>
  );
};

/** Card wrapper — darker, flatter than GlowCard. Used for in-section rows. */
export const GlassCard: React.FC<{
  children: React.ReactNode;
  tint?: string;
  padding?: number | string;
  radius?: number;
  onClick?: () => void;
  sx?: SxProps<Theme>;
}> = ({ children, tint, padding = 12, radius = 16, onClick, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // True iOS glassmorphism — TRANSLUCENT cards over the body::after paw
  // print watermark (src/index.css). NO backdrop-filter blur (that turns
  // the card opaque-white and hides the paws — see CLAUDE.md memory).
  // The bg is a low-alpha fill in both modes so paws bleed through.
  // Glow shadows give color identity without sacrificing transparency.
  const bg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)';
  const border = isDark
    ? tint
      ? alphaHex(tint, 0.32)
      : 'rgba(255,255,255,0.10)'
    : tint
      ? alphaHex(tint, 0.28)
      : 'rgba(15,23,42,0.08)';

  // Default (no-tint) shadows — subtle depth that doesn't fight the
  // transparency.
  const defaultShadow = isDark
    ? '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.25)'
    : '0 1px 2px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.04)';

  // Tinted shadow: the iOS "glow" — wider blur + tint color escaping the
  // card edges. Higher alpha in dark so the color reads against the
  // (also dark) page. No filter on the card itself; this is purely an
  // outset shadow.
  const tintedShadow = tint
    ? isDark
      ? `0 1px 2px rgba(0,0,0,0.4), 0 8px 32px ${alphaHex(tint, 0.30)}, 0 0 0 1px ${alphaHex(tint, 0.08)}`
      : `0 1px 2px rgba(15,23,42,0.04), 0 6px 20px ${alphaHex(tint, 0.16)}`
    : defaultShadow;

  const hoverBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.7)';
  const hoverBorder = tint
    ? alphaHex(tint, isDark ? 0.5 : 0.4)
    : isDark
      ? 'rgba(255,255,255,0.18)'
      : 'rgba(15,23,42,0.12)';
  const hoverShadow = tint
    ? isDark
      ? `0 2px 4px rgba(0,0,0,0.4), 0 12px 40px ${alphaHex(tint, 0.42)}, 0 0 0 1px ${alphaHex(tint, 0.15)}`
      : `0 2px 4px rgba(15,23,42,0.06), 0 10px 28px ${alphaHex(tint, 0.26)}`
    : isDark
      ? '0 2px 4px rgba(0,0,0,0.5), 0 12px 32px rgba(0,0,0,0.35)'
      : '0 2px 4px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.06)';

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        borderRadius: `${radius}px`,
        padding: typeof padding === 'number' ? `${padding}px` : padding,
        bgcolor: bg,
        border: '1px solid',
        borderColor: border,
        boxShadow: tintedShadow,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.15s, border-color 0.15s, box-shadow 0.2s, transform 0.15s',
        '&:hover': onClick
          ? {
              bgcolor: hoverBg,
              borderColor: hoverBorder,
              boxShadow: hoverShadow,
              transform: 'translateY(-1px)',
            }
          : undefined,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Collapsible section — matches iOS CareRemindersSection / MedsSection / VaxSection shell.
 * Header: emoji tile | title + optional count | trailing right slot | chevron.
 */
interface CollapsibleSectionProps {
  emoji?: string;
  icon?: React.ReactNode;
  title: string;
  tint: string;
  count?: number | null;
  right?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  /** Optional: used to persist open/closed across re-mounts (not wired to storage). */
  sectionKey?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  emoji,
  icon,
  title,
  tint,
  count,
  right,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        borderRadius: `20px`,
        bgcolor: isDark ? glassSurface.light.cardBgDark : glassSurface.light.cardBgLight,
        backgroundImage: isDark ? glassSurface.light.sheenDark : glassSurface.light.sheenLight,
        border: '1px solid',
        borderColor: isDark ? glassSurface.light.borderDark : glassSurface.light.borderLight,
        boxShadow: glowShadow(tint, isDark),
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.15s',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' },
        }}
      >
        <EmojiTile emoji={emoji} icon={icon} tint={tint} />
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
            {title}
          </Typography>
          {count != null && (
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
              ({count})
            </Typography>
          )}
        </Box>
        {right !== undefined && <Box sx={{ display: 'flex', alignItems: 'center' }}>{right}</Box>}
        <ChevronIcon
          sx={{
            color: 'text.secondary',
            fontSize: 22,
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.25s',
          }}
        />
      </Box>
      <Collapse in={open} timeout={280}>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

/** Format a date-ish value to "MMM d, yyyy" */
export function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtMonthDay(d: Date | string | null | undefined): string {
  if (!d) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function yearOf(d: Date | string | null | undefined): number | null {
  if (!d) return null;
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return null;
  return date.getFullYear();
}
